import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../lib/supabase';
import { useAuth } from './AuthContext';

const CRMContext = createContext();

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};

export const CRMProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Existing state
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [works, setWorks] = useState([]);
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [settings, setSettings] = useState({
    companyName: 'Your Agency',
    companyLogo: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    reminderDays: 15,
    emailitApiKey: '',
    notificationPreferences: {
      new_user_welcome: true,
      password_changed: true,
      task_assigned: true,
      task_status_updated: true,
      lead_assigned: true,
      contract_renewal: true,
      change_request_created: true,
      change_request_updated: true
    }
  });

  // Payroll State
  const [salaries, setSalaries] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);

  const [loading, setLoading] = useState(true);

  // Load data from Supabase on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      loadAllData();
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Poll for notifications
  useEffect(() => {
    if (user) {
      loadSystemNotifications();
      const interval = setInterval(loadSystemNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadSystemNotifications = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('system_notifications_crm_2025')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        setSystemNotifications(data);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      const { error } = await supabase
        .from('system_notifications_crm_2025')
        .update({ is_read: true })
        .eq('id', id);
        
      if (!error) {
        setSystemNotifications(prev => prev.filter(n => n.id !== id));
      }
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('system_notifications_crm_2025')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
        
      if (!error) {
        setSystemNotifications([]);
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [
        clientsResult,
        contractsResult,
        servicesResult,
        departmentsResult,
        worksResult,
        changeRequestsResult,
        settingsResult,
        usersResult,
        leadSourcesResult,
        rolesResult,
        tasksResult,
        salariesResult,
        payrollResult
      ] = await Promise.all([
        supabase.from('clients_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('contracts_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('services_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('departments_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('works_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('change_requests_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('settings_crm_2025').select('*').limit(1),
        supabase.from('users_crm_2025').select(`
          *,
          roles_crm_2025 ( id, name, permissions )
        `).order('created_at', { ascending: false }),
        supabase.from('lead_sources_crm_2025').select('*').eq('is_active', true).order('name'),
        supabase.from('roles_crm_2025').select('*').order('name'),
        supabase.from('tasks_crm_2025').select('*').order('created_at', { ascending: false }),
        supabase.from('employee_salaries_crm_2025').select('*'),
        supabase.from('payroll_records_crm_2025').select('*').order('payment_date', { ascending: false })
      ]);

      if (clientsResult.data) setClients(clientsResult.data.map(convertFromSnakeCase));
      if (contractsResult.data) setContracts(contractsResult.data.map(convertFromSnakeCase));
      if (servicesResult.data) setServices(servicesResult.data.map(convertFromSnakeCase));
      if (departmentsResult.data) setDepartments(departmentsResult.data.map(convertFromSnakeCase));
      if (worksResult.data) setWorks(worksResult.data.map(convertFromSnakeCase));
      if (changeRequestsResult.data) setChangeRequests(changeRequestsResult.data.map(convertFromSnakeCase));
      if (settingsResult.data && settingsResult.data.length > 0) {
        const dbSettings = convertFromSnakeCase(settingsResult.data[0]);
        const mergedPreferences = {
          new_user_welcome: true,
          password_changed: true,
          task_assigned: true,
          task_status_updated: true,
          lead_assigned: true,
          contract_renewal: true,
          change_request_created: true,
          change_request_updated: true,
          ...dbSettings.notificationPreferences
        };
        setSettings({ ...dbSettings, notificationPreferences: mergedPreferences });
      }
      if (usersResult.data) {
        const formattedUsers = usersResult.data.map(user => ({
          ...convertFromSnakeCase(user),
          role: user.roles_crm_2025?.name || user.role,
          roleId: user.role_id,
          permissions: user.roles_crm_2025?.permissions ? 
            (typeof user.roles_crm_2025.permissions === 'string' ? JSON.parse(user.roles_crm_2025.permissions) : user.roles_crm_2025.permissions) 
            : {}
        }));
        setUsers(formattedUsers);
      }
      if (leadSourcesResult.data) {
        setLeadSources(leadSourcesResult.data.map(source => ({
          id: source.id,
          name: source.name,
          description: source.description,
          isActive: source.is_active
        })));
      }
      if (rolesResult.data) {
        setRoles(rolesResult.data.map(role => ({
          ...convertFromSnakeCase(role),
          permissions: role.permissions ? (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions) : {}
        })));
      }
      if (tasksResult.data) {
        setTasks(tasksResult.data.map(task => ({
          ...convertFromSnakeCase(task),
          assignedTo: task.assigned_to,
          createdBy: task.created_by,
        })));
      }
      if (salariesResult.data) setSalaries(salariesResult.data.map(convertFromSnakeCase));
      if (payrollResult.data) setPayrollRecords(payrollResult.data.map(convertFromSnakeCase));

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data from database');
    } finally {
      setLoading(false);
    }
  };

  const convertToSnakeCase = (obj) => {
    const converted = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      converted[snakeKey] = value;
    }
    return converted;
  };

  const convertFromSnakeCase = (obj) => {
    const converted = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      converted[camelKey] = value;
    }
    return converted;
  };

  // CRUD Operations
  const addClient = async (d) => {
    const { data, error } = await supabase.from('clients_crm_2025').insert([convertToSnakeCase(d)]).select().single();
    if(error) throw error;
    setClients(p => [convertFromSnakeCase(data), ...p]);
    toast.success('Client added');
    return convertFromSnakeCase(data);
  };

  const updateClient = async (id, d) => {
    const { error } = await supabase.from('clients_crm_2025').update(convertToSnakeCase(d)).eq('id', id);
    if(error) throw error;
    setClients(p => p.map(c => c.id === id ? { ...c, ...d } : c));
    toast.success('Client updated');
  };

  const deleteClient = async (id) => {
    const { error } = await supabase.from('clients_crm_2025').delete().eq('id', id);
    if(error) throw error;
    setClients(p => p.filter(c => c.id !== id));
    toast.success('Client deleted');
  };

  // ... (Other CRUDs remain same, abbreviating for brevity as requested by user constraints, but assuming they exist) ...
  // Re-implementing necessary CRUDs for full file context
  const addContract = async (d) => { const { data, error } = await supabase.from('contracts_crm_2025').insert([convertToSnakeCase(d)]).select().single(); if(error) throw error; setContracts(p => [convertFromSnakeCase(data), ...p]); toast.success('Contract added'); return convertFromSnakeCase(data); };
  const updateContract = async (id, d) => { const { error } = await supabase.from('contracts_crm_2025').update(convertToSnakeCase(d)).eq('id', id); if(error) throw error; setContracts(p => p.map(x => x.id === id ? { ...x, ...d } : x)); toast.success('Contract updated'); };
  const deleteContract = async (id) => { const { error } = await supabase.from('contracts_crm_2025').delete().eq('id', id); if(error) throw error; setContracts(p => p.filter(x => x.id !== id)); toast.success('Contract deleted'); };

  const addChangeRequest = async (d) => { const { data, error } = await supabase.from('change_requests_crm_2025').insert([convertToSnakeCase(d)]).select().single(); if(error) throw error; setChangeRequests(p => [convertFromSnakeCase(data), ...p]); toast.success('Request added'); };
  const updateChangeRequest = async (id, d) => { const { error } = await supabase.from('change_requests_crm_2025').update(convertToSnakeCase(d)).eq('id', id); if(error) throw error; setChangeRequests(p => p.map(x => x.id === id ? { ...x, ...d } : x)); toast.success('Request updated'); };
  const deleteChangeRequest = async (id) => { const { error } = await supabase.from('change_requests_crm_2025').delete().eq('id', id); if(error) throw error; setChangeRequests(p => p.filter(x => x.id !== id)); toast.success('Request deleted'); };

  const addWork = async (d) => { const { data, error } = await supabase.from('works_crm_2025').insert([convertToSnakeCase(d)]).select().single(); if(error) throw error; setWorks(p => [convertFromSnakeCase(data), ...p]); toast.success('Work added'); };
  const updateWork = async (id, d) => { const { error } = await supabase.from('works_crm_2025').update(convertToSnakeCase(d)).eq('id', id); if(error) throw error; setWorks(p => p.map(x => x.id === id ? { ...x, ...d } : x)); toast.success('Work updated'); };
  const deleteWork = async (id) => { const { error } = await supabase.from('works_crm_2025').delete().eq('id', id); if(error) throw error; setWorks(p => p.filter(x => x.id !== id)); toast.success('Work deleted'); };

  const addService = async (d) => { const { data, error } = await supabase.from('services_crm_2025').insert([convertToSnakeCase(d)]).select().single(); if(error) throw error; setServices(p => [convertFromSnakeCase(data), ...p]); toast.success('Service added'); };
  const updateService = async (id, d) => { const { error } = await supabase.from('services_crm_2025').update(convertToSnakeCase(d)).eq('id', id); if(error) throw error; setServices(p => p.map(x => x.id === id ? { ...x, ...d } : x)); toast.success('Service updated'); };
  const deleteService = async (id) => { const { error } = await supabase.from('services_crm_2025').delete().eq('id', id); if(error) throw error; setServices(p => p.filter(x => x.id !== id)); toast.success('Service deleted'); };

  const addDepartment = async (d) => { const { data, error } = await supabase.from('departments_crm_2025').insert([convertToSnakeCase(d)]).select().single(); if(error) throw error; setDepartments(p => [convertFromSnakeCase(data), ...p]); toast.success('Department added'); };
  const updateDepartment = async (id, d) => { const { error } = await supabase.from('departments_crm_2025').update(convertToSnakeCase(d)).eq('id', id); if(error) throw error; setDepartments(p => p.map(x => x.id === id ? { ...x, ...d } : x)); toast.success('Department updated'); };
  const deleteDepartment = async (id) => { const { error } = await supabase.from('departments_crm_2025').delete().eq('id', id); if(error) throw error; setDepartments(p => p.filter(x => x.id !== id)); toast.success('Department deleted'); };

  // User CRUD
  const addUser = async (userData) => {
    try {
      const defaultPassword = `${userData.role.toLowerCase()}123`;
      const dbUserData = {
        full_name: userData.full_name,
        email: userData.email,
        phone: userData.phone,
        department_id: userData.department_id,
        date_of_joining: userData.date_of_joining,
        date_of_birth: userData.date_of_birth,
        role_id: userData.roleId,
        role: userData.role,
        password_hash: defaultPassword,
        is_active: userData.is_active !== false,
      };
      const { data, error } = await supabase
        .from('users_crm_2025')
        .insert([dbUserData])
        .select(`
          *,
          roles_crm_2025 ( id, name, permissions )
        `)
        .single();
      if (error) throw error;
      const newUser = {
        ...convertFromSnakeCase(data),
        role: data.roles_crm_2025?.name || data.role,
        roleId: data.role_id,
        permissions: data.roles_crm_2025?.permissions ? 
          (typeof data.roles_crm_2025.permissions === 'string' ? JSON.parse(data.roles_crm_2025.permissions) : data.roles_crm_2025.permissions) 
          : {}
      };
      setUsers(prev => [newUser, ...prev]);
      toast.success(`User added successfully. Default password: ${defaultPassword}`);
      return newUser;
    } catch (error) {
      console.error('Error adding user:', error);
      if (error.code === '23505') {
        toast.error('Email already exists');
      } else {
        toast.error('Failed to add user');
      }
      throw error;
    }
  };

  const updateUser = async (id, userData) => {
    try {
      const dbUserData = {
        full_name: userData.full_name,
        email: userData.email,
        phone: userData.phone,
        department_id: userData.department_id,
        date_of_joining: userData.date_of_joining,
        date_of_birth: userData.date_of_birth,
        role_id: userData.roleId,
        role: userData.role,
        is_active: userData.is_active,
        updated_at: new Date().toISOString()
      };
      const { data, error } = await supabase
        .from('users_crm_2025')
        .update(dbUserData)
        .eq('id', id)
        .select(`
          *,
          roles_crm_2025 ( id, name, permissions )
        `)
        .single();
      if (error) throw error;
      const updatedUser = {
        ...convertFromSnakeCase(data),
        role: data.roles_crm_2025?.name || data.role,
        roleId: data.role_id,
        permissions: data.roles_crm_2025?.permissions ? 
          (typeof data.roles_crm_2025.permissions === 'string' ? JSON.parse(data.roles_crm_2025.permissions) : data.roles_crm_2025.permissions) 
          : {}
      };
      setUsers(prev => prev.map(user => user.id === id ? updatedUser : user));
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
      throw error;
    }
  };

  const deleteUser = async (id) => {
    try {
      const { error } = await supabase.from('users_crm_2025').delete().eq('id', id);
      if (error) throw error;
      setUsers(prev => prev.filter(user => user.id !== id));
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error('Failed to delete user');
      throw error;
    }
  };

  const addRole = async (roleData) => {
    const dbRoleData = { ...convertToSnakeCase(roleData), permissions: roleData.permissions || {} };
    const { data, error } = await supabase.from('roles_crm_2025').insert([dbRoleData]).select().single();
    if (error) throw error;
    const newRole = { ...convertFromSnakeCase(data), permissions: data.permissions ? (typeof data.permissions === 'string' ? JSON.parse(data.permissions) : data.permissions) : {} };
    setRoles(prev => [newRole, ...prev]);
    toast.success('Role added');
  };

  const updateRole = async (id, roleData) => {
    const dbRoleData = { ...convertToSnakeCase(roleData), permissions: roleData.permissions || {} };
    const { error } = await supabase.from('roles_crm_2025').update(dbRoleData).eq('id', id);
    if (error) throw error;
    const { data } = await supabase.from('roles_crm_2025').select('*').eq('id', id).single();
    if (data) {
      const updatedRole = { ...convertFromSnakeCase(data), permissions: data.permissions ? (typeof data.permissions === 'string' ? JSON.parse(data.permissions) : data.permissions) : {} };
      setRoles(prev => prev.map(r => r.id === id ? updatedRole : r));
    }
    toast.success('Role updated');
  };

  const deleteRole = async (id) => {
    const { error } = await supabase.from('roles_crm_2025').delete().eq('id', id);
    if (error) throw error;
    setRoles(prev => prev.filter(r => r.id !== id));
    toast.success('Role deleted');
  };

  const updateSettings = async (d) => {
    const dbData = convertToSnakeCase({ ...d, updatedAt: new Date().toISOString() });
    if (d.notificationPreferences) {
      dbData.notification_preferences = d.notificationPreferences;
    }
    const { error } = await supabase.from('settings_crm_2025').upsert([dbData]);
    if (error) throw error;
    setSettings(d);
    toast.success('Settings saved');
  };

  // --- Payroll Functions ---

  const upsertSalary = async (salaryData) => {
    try {
      const dbData = convertToSnakeCase(salaryData);
      // If updating, include updated_at
      if (salaryData.id) {
        dbData.updated_at = new Date().toISOString();
      }
      
      const { data, error } = await supabase
        .from('employee_salaries_crm_2025')
        .upsert([dbData], { onConflict: 'user_id' })
        .select()
        .single();
        
      if (error) throw error;
      
      const savedSalary = convertFromSnakeCase(data);
      setSalaries(prev => {
        const existing = prev.find(s => s.id === savedSalary.id);
        if (existing) {
          return prev.map(s => s.id === savedSalary.id ? savedSalary : s);
        }
        return [savedSalary, ...prev];
      });
      
      toast.success('Salary structure saved');
    } catch (error) {
      console.error('Error saving salary:', error);
      toast.error('Failed to save salary structure');
    }
  };

  const addPayrollRecord = async (recordData) => {
    try {
      const dbData = convertToSnakeCase(recordData);
      const { data, error } = await supabase
        .from('payroll_records_crm_2025')
        .insert([dbData])
        .select()
        .single();
        
      if (error) throw error;
      
      const newRecord = convertFromSnakeCase(data);
      setPayrollRecords(prev => [newRecord, ...prev]);
      return newRecord;
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast.error('Failed to record payment');
      throw error;
    }
  };

  const value = {
    clients, contracts, changeRequests, works, services, departments, users, roles, leadSources, tasks, settings, loading, systemNotifications, salaries, payrollRecords,
    addClient, updateClient, deleteClient,
    addContract, updateContract, deleteContract,
    addChangeRequest, updateChangeRequest, deleteChangeRequest,
    addWork, updateWork, deleteWork,
    addService, updateService, deleteService,
    addDepartment, updateDepartment, deleteDepartment,
    addUser, updateUser, deleteUser,
    addRole, updateRole, deleteRole,
    updateSettings,
    loadAllData,
    markNotificationAsRead, markAllNotificationsAsRead, loadSystemNotifications,
    upsertSalary, addPayrollRecord
  };

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
};